import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Typography,
  CircularProgress,
  Alert,
  TextField,
  Stack,
  IconButton,
} from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import { useState, useEffect, useCallback } from 'react';
import {
  getCycleRewardTiers,
  createCycleRewardTier,
  updateCycleRewardTier,
  deleteCycleRewardTier,
  LeaderboardCycle,
  CycleRewardTier,
} from '../../../services/redpacketService';
import { useTranslate, useNotify } from 'react-admin';
import { PermissionButton } from '../../../../../routes/components/permission-button';

interface RewardTiersDialogProps {
  record: LeaderboardCycle;
  onClose: () => void;
}

type TierDraft = {
  minRank: string;
  maxRank: string;
  rewardAmount: string;
  description: string;
};

const emptyDraft = (): TierDraft => ({
  minRank: '',
  maxRank: '',
  rewardAmount: '',
  description: '',
});

const RewardTiersDialog = ({ record, onClose }: RewardTiersDialogProps) => {
  const t = useTranslate();
  const notify = useNotify();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [tiers, setTiers] = useState<CycleRewardTier[]>([]);
  const [formOpen, setFormOpen] = useState(false);
  const [editing, setEditing] = useState<CycleRewardTier | null>(null);
  const [draft, setDraft] = useState<TierDraft>(emptyDraft());
  const [saving, setSaving] = useState(false);

  const loadTiers = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const list = await getCycleRewardTiers(record.id);
      setTiers(Array.isArray(list) ? list : []);
    } catch (err: any) {
      setError(err.message || t('module.redpacket.rewardTiersLoadFailed'));
      setTiers([]);
    } finally {
      setLoading(false);
    }
  }, [record.id, t]);

  useEffect(() => {
    loadTiers();
  }, [loadTiers]);

  const openCreate = () => {
    setEditing(null);
    setDraft(emptyDraft());
    setFormOpen(true);
  };

  const openEdit = (tier: CycleRewardTier) => {
    setEditing(tier);
    setDraft({
      minRank: String(tier.minRank),
      maxRank: String(tier.maxRank),
      rewardAmount: tier.rewardAmount,
      description: tier.description ?? '',
    });
    setFormOpen(true);
  };

  const closeForm = () => {
    setFormOpen(false);
    setEditing(null);
    setDraft(emptyDraft());
  };

  const handleSaveTier = async () => {
    const minRank = Number(draft.minRank);
    const maxRank = Number(draft.maxRank);
    if (!Number.isFinite(minRank) || !Number.isFinite(maxRank) || minRank < 1 || maxRank < minRank) {
      notify(t('module.redpacket.rewardTierRankInvalid'), { type: 'warning' });
      return;
    }
    if (!draft.rewardAmount.trim()) {
      notify(t('module.redpacket.rewardAmountRequired'), { type: 'warning' });
      return;
    }
    try {
      setSaving(true);
      if (editing) {
        await updateCycleRewardTier({
          id: editing.id,
          minRank,
          maxRank,
          rewardAmount: draft.rewardAmount.trim(),
          description: draft.description.trim() || null,
        });
        notify(t('module.redpacket.rewardTierUpdated'), { type: 'success' });
      } else {
        await createCycleRewardTier({
          cycleId: record.id,
          minRank,
          maxRank,
          rewardAmount: draft.rewardAmount.trim(),
          description: draft.description.trim() || null,
        });
        notify(t('module.redpacket.rewardTierCreated'), { type: 'success' });
      }
      closeForm();
      await loadTiers();
    } catch (err: any) {
      notify(err.message || t('module.redpacket.rewardTierSaveFailed'), { type: 'error' });
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (tier: CycleRewardTier) => {
    if (!window.confirm(t('module.redpacket.confirmDeleteRewardTier'))) return;
    try {
      await deleteCycleRewardTier(tier.id);
      notify(t('module.redpacket.rewardTierDeleted'), { type: 'success' });
      await loadTiers();
    } catch (err: any) {
      notify(err.message || t('module.redpacket.rewardTierDeleteFailed'), { type: 'error' });
    }
  };

  return (
    <Dialog open maxWidth="md" fullWidth>
      <DialogTitle>
        {t('module.redpacket.configRewardTiers')}
        <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
          {t('module.redpacket.cycleNumberLabel')}
          {record.cycleNumber} · {record.status}
        </Typography>
      </DialogTitle>
      <DialogContent>
        <Box sx={{ mb: 2, display: 'flex', justifyContent: 'flex-end' }}>
          {!formOpen ? (
            <PermissionButton resource="redpacket" action="create">
              <Button variant="contained" size="small" onClick={openCreate}>
                {t('module.redpacket.addRewardTier')}
              </Button>
            </PermissionButton>
          ) : (
            <Button size="small" onClick={closeForm}>
              {t('actions.cancel')}
            </Button>
          )}
        </Box>
        {loading ? (
          <Box display="flex" justifyContent="center" py={4}>
            <CircularProgress />
          </Box>
        ) : error ? (
          <Alert severity="error">{error}</Alert>
        ) : (
          <TableContainer component={Paper} variant="outlined">
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>{t('module.redpacket.minRank')}</TableCell>
                  <TableCell>{t('module.redpacket.maxRank')}</TableCell>
                  <TableCell align="right">{t('module.redpacket.prizeAmountCol')}</TableCell>
                  <TableCell>{t('module.redpacket.descriptionCol')}</TableCell>
                  <TableCell align="right">{t('module.redpacket.operation')}</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {tiers.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={5} align="center">
                      {t('module.redpacket.noRewardTiers')}
                    </TableCell>
                  </TableRow>
                ) : (
                  tiers.map((tier) => (
                    <TableRow key={tier.id}>
                      <TableCell>{tier.minRank}</TableCell>
                      <TableCell>{tier.maxRank}</TableCell>
                      <TableCell align="right">{tier.rewardAmount}</TableCell>
                      <TableCell>{tier.description ?? '—'}</TableCell>
                      <TableCell align="right">
                        <IconButton size="small" onClick={() => openEdit(tier)} aria-label="edit">
                          <EditIcon fontSize="small" />
                        </IconButton>
                        <PermissionButton resource="redpacket" action="delete">
                          <IconButton
                            size="small"
                            onClick={() => handleDelete(tier)}
                            aria-label="delete"
                          >
                            <DeleteOutlineIcon fontSize="small" />
                          </IconButton>
                        </PermissionButton>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
        )}

        {formOpen && (
          <Box sx={{ mt: 3, p: 2, border: 1, borderColor: 'divider', borderRadius: 1 }}>
            <Typography variant="subtitle2" sx={{ mb: 2 }}>
              {editing ? t('module.redpacket.editRewardTier') : t('module.redpacket.addRewardTier')}
            </Typography>
            <Stack spacing={2}>
              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
                <TextField
                  label={t('module.redpacket.minRank')}
                  value={draft.minRank}
                  onChange={(e) => setDraft((d) => ({ ...d, minRank: e.target.value }))}
                  size="small"
                  fullWidth
                  type="number"
                />
                <TextField
                  label={t('module.redpacket.maxRank')}
                  value={draft.maxRank}
                  onChange={(e) => setDraft((d) => ({ ...d, maxRank: e.target.value }))}
                  size="small"
                  fullWidth
                  type="number"
                />
              </Stack>
              <TextField
                label={t('module.redpacket.prizeAmountCol')}
                value={draft.rewardAmount}
                onChange={(e) => setDraft((d) => ({ ...d, rewardAmount: e.target.value }))}
                size="small"
                fullWidth
              />
              <TextField
                label={t('module.redpacket.descriptionCol')}
                value={draft.description}
                onChange={(e) => setDraft((d) => ({ ...d, description: e.target.value }))}
                size="small"
                fullWidth
                multiline
                minRows={2}
              />
              <Stack direction="row" spacing={1} justifyContent="flex-end">
                <Button onClick={closeForm} disabled={saving}>
                  {t('actions.cancel')}
                </Button>
                <PermissionButton resource="redpacket" action={editing ? 'update' : 'create'}>
                  <Button variant="contained" onClick={handleSaveTier} disabled={saving}>
                    {t('module.redpacket.save')}
                  </Button>
                </PermissionButton>
              </Stack>
            </Stack>
          </Box>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>{t('module.redpacket.close')}</Button>
      </DialogActions>
    </Dialog>
  );
};

export default RewardTiersDialog;
